import type { CreateSessionInput } from '../api/endpoints/sessions';
import type { Service } from '../api/schemas';
import { parsePriceInput } from './money';

const MINUTE_MS = 60_000;
export const MAX_CLASS_CAPACITY = 500;
export const MAX_CLASS_DURATION_MIN = 24 * 60;
/** The API caps the cancellation window at 43,200 minutes: 30 days. */
export const MAX_CANCELLATION_HOURS = 30 * 24;

export type ClassPaymentMode = 'pay_at_studio' | 'prepay_required';

export type ClassDraft = {
  serviceId: string;
  instructorId: string;
  /** `datetime-local` value, in the browser's zone. */
  startsAt: string;
  durationMin: string;
  capacity: string;
  priceInput: string;
  /** Whole hours before the class a customer may still cancel. */
  cutoffHours: string;
  paymentMode: ClassPaymentMode;
};

type Result = { ok: true; input: CreateSessionInput } | { ok: false; message: string };

/** Turns the form draft into the API body, or names the first thing to fix. */
export function classSessionInput(
  draft: ClassDraft,
  services: readonly Service[],
  now: Date,
  prepayAvailable: boolean,
): Result {
  if (!services.some((service) => service.id === draft.serviceId)) return { ok: false, message: 'Elige la clase.' };
  if (!draft.instructorId) return { ok: false, message: 'Elige quién da la clase.' };
  const starts = new Date(draft.startsAt);
  if (!draft.startsAt || Number.isNaN(starts.getTime())) return { ok: false, message: 'Elige fecha y hora.' };
  if (starts.getTime() <= now.getTime()) return { ok: false, message: 'La clase tiene que ser más adelante.' };
  const durationMin = Number(draft.durationMin);
  if (!Number.isInteger(durationMin) || durationMin < 1 || durationMin > MAX_CLASS_DURATION_MIN) {
    return { ok: false, message: 'Revisa la duración en minutos.' };
  }
  const capacity = Number(draft.capacity);
  if (!Number.isInteger(capacity) || capacity < 1 || capacity > MAX_CLASS_CAPACITY) {
    return { ok: false, message: `El cupo va de 1 a ${MAX_CLASS_CAPACITY}.` };
  }
  const price = parsePriceInput(draft.priceInput);
  if (!price.ok) return { ok: false, message: price.message };
  const cutoffHours = Number(draft.cutoffHours);
  if (!Number.isInteger(cutoffHours) || cutoffHours < 0 || cutoffHours > MAX_CANCELLATION_HOURS) {
    return { ok: false, message: `La cancelación va de 0 a ${MAX_CANCELLATION_HOURS} horas antes.` };
  }
  if (draft.paymentMode === 'prepay_required' && !prepayAvailable) {
    return { ok: false, message: 'Activa los cobros por adelantado en Negocio antes de exigir el pago de la clase.' };
  }
  return {
    ok: true,
    input: {
      serviceId: draft.serviceId,
      instructorId: draft.instructorId,
      startsAt: starts.toISOString(),
      endsAt: new Date(starts.getTime() + durationMin * MINUTE_MS).toISOString(),
      capacity,
      resourceIds: [],
      priceCents: price.cents,
      policy: { cancellationCutoffMin: cutoffHours * 60, paymentMode: draft.paymentMode },
    },
  };
}
